import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import styled from '@emotion/styled';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import isBlank from 'is-blank';
import { AppUI } from '@seedlang/state';
import Alert from 'components/alert';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  input[type="password"] {
    text-align: left;
    width: 100%;
    box-sizing: border-box;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 275px;
  min-width: 0;
`;

const Label = styled.div`
  font-size: 14px;
`;

@observer
class UserResetPassword extends React.Component {
  @observable password;
  @observable passwordConfirmation;
  @observable resetPasswordToken;
  @observable errorMessage = '';
  @observable showResetLink = false;
  @observable displaySuccessMessage = false;
  @observable showPasswordLengthHint = false;
  @observable showPasswordMismatch = false;
  passwordLengthHintTimeout;
  passwordMismatchTimeout;

  constructor(props) {
    super(props);
    this.resetPasswordToken = new URLSearchParams(window.location.search).get('reset_password_token');
  }

  @computed get disabled() {
    return isBlank(this.password) || this.password !== this.passwordConfirmation || this.password.length < 8;
  }
  componentWillUnmount() {
    clearTimeout(this.passwordLengthHintTimeout);
    clearTimeout(this.passwordMismatchTimeout);
  }
  onPasswordChange = (e) => {
    this.password = e.currentTarget.value;
    this.schedulePasswordMismatch();
    clearTimeout(this.passwordLengthHintTimeout);
    this.showPasswordLengthHint = false;
    if (this.password.length > 0 && this.password.length < 8) {
      this.passwordLengthHintTimeout = setTimeout(() => {
        this.showPasswordLengthHint = true;
      }, 1000);
    }
  }
  onPasswordConfirmationChange = (e) => {
    this.passwordConfirmation = e.currentTarget.value;
    this.schedulePasswordMismatch();
  }
  schedulePasswordMismatch = () => {
    clearTimeout(this.passwordMismatchTimeout);
    this.showPasswordMismatch = false;
    if (this.passwordConfirmation && this.password !== this.passwordConfirmation) {
      this.passwordMismatchTimeout = setTimeout(() => {
        this.showPasswordMismatch = true;
      }, 1000);
    }
  }
  onSubmitPassword = () => {
    this.errorMessage = '';
    this.showResetLink = false;
    this.displaySuccessMessage = false;
    AppUI.userStore.resetPassword({
      data: {
        password: this.password,
        passwordConfirmation: this.passwordConfirmation,
        resetPasswordToken: this.resetPasswordToken,
      },
      failureCallback: this.onRequestError,
      onRequestError: this.onRequestError,
    }, this.afterSubmitPassword);
  }
  onRequestError = () => {
    this.errorMessage = 'We could not complete your password reset. Please check your connection and try again. If the problem continues, contact us for help.';
  }
  afterSubmitPassword = (resp) => {
    this.errorMessage = '';
    this.showResetLink = false;
    if (resp.success) {
      this.displaySuccessMessage = true;
    } else {
      const tokenErrors = {
        missing_reset_token: 'Your reset link is incomplete. Please open the full link from your email.',
        invalid_reset_token: 'This reset link is no longer valid. It may have already been used or replaced by a newer link.',
        expired_reset_token: 'Your reset link has expired. Reset links are valid for one hour.',
      };
      this.showResetLink = Boolean(tokenErrors[resp.errorCode]) || resp.message === 'Invalid or expired reset token';
      this.errorMessage = tokenErrors[resp.errorCode] || (this.showResetLink
        ? 'This reset link is invalid or has expired.'
        : resp.message || 'We could not change your password. Please try again or contact us for help.');
    }
  }

  render() {
    return (
      <Wrapper>
        <h3>Reset Your Password</h3>
        {
          this.displaySuccessMessage &&
            <Alert
              background={Theme.green}
            >
              Your password has been changed. Please sign in with your new password <a href='/onboarding/sign_in'>here</a>.
            </Alert>
        }
        {
          this.errorMessage &&
            <Alert>
              {this.errorMessage}{' '}
              {this.showResetLink ? <a href='/onboarding/forgot_password'>Request a new reset link</a> : <a href='/contact'>Contact Us</a>}
            </Alert>
        }
        {
          !this.resetPasswordToken &&
            <Alert
              margin='20px 0 0 0'
            >
              Your reset link is incomplete. Please open the full link from your email or <a href='/onboarding/forgot_password'>request a new reset link</a>.
            </Alert>
        }
        {
          this.resetPasswordToken &&
            <FormWrapper>
              <Label>New Password</Label>
              <input
                type='password'
                value={this.password || ''}
                onChange={this.onPasswordChange}
                style={{marginBottom: 10}}
              />
              {
                this.showPasswordLengthHint &&
                  <Alert>Use at least 8 characters</Alert>
              }
              <Label>Password Confirmation</Label>
              <input
                type='password'
                value={this.passwordConfirmation || ''}
                onChange={this.onPasswordConfirmationChange}
              />
              {
                this.showPasswordMismatch &&
                  <Alert margin='10px 0 0 0'>The passwords do not match.</Alert>
              }
              <Button
                margin='20px 0 0 0'
                onClick={this.onSubmitPassword}
                backgroundColor={Theme.green}
                width='100%'
                disabled={this.disabled}
              >
                Submit
              </Button>
            </FormWrapper>
        }
      </Wrapper>
    );
  }
}

export default UserResetPassword;
